import type { Ref } from "react";
import { cn, generateTextShadow, REWARD_ICONS } from "@/lib/utils";

interface PrizeItem {
  icon: string;
  value: string | number | React.ReactNode;
  alt?: string;
  isPositive?: boolean; // If provided, shows + or - sign. If undefined, no sign.
}

interface PrizeWrapperProps {
  prizes: PrizeItem[];
  className?: string;
  isDoubleTrophyActive?: boolean;
  iconSize?: string; // e.g., "size-8", "size-10"
  textSize?: string; // e.g., "text-base", "text-2xl"
  usdcPrizeRef?: Ref<HTMLDivElement>;
}

export function PrizeWrapper({
  prizes,
  className,
  isDoubleTrophyActive,
  iconSize = "w-10",
  textSize = "text-2xl",
  usdcPrizeRef,
}: PrizeWrapperProps) {
  // Filter out prizes without values
  const validPrizes = prizes.filter(
    (prize) =>
      prize.value !== null &&
      prize.value !== undefined &&
      prize.value !== "" &&
      prize.value !== 0,
  );

  if (validPrizes.length === 0) return null;

  return (
    <div
      className={cn("py-4 flex items-center justify-center gap-4", className)}
    >
      {validPrizes.map((prize, index) => (
        <div
          key={index}
          ref={prize.icon === REWARD_ICONS[0] ? usdcPrizeRef : undefined}
          className="flex flex-col items-center justify-center gap-2"
        >
          <img
            src={prize.icon}
            alt={prize.alt || "Prize"}
            className={iconSize}
            style={{ imageRendering: "pixelated" }}
          />
          {typeof prize.value === "object" ? (
            prize.value
          ) : (
            <span
              className={cn(
                `${textSize} font-bold -mt-3.5 text-white text-shadow-black leading-4`,
              )}
            >
              {prize.isPositive !== undefined && (prize.isPositive ? "+" : "-")}
              {typeof prize.value === "number"
                ? prize.value % 1 === 0
                  ? prize.value.toString()
                  : prize.value.toFixed(2)
                : prize.value}
            </span>
          )}
        </div>
      ))}
      {isDoubleTrophyActive &&
        (prizes.find((p) => p.icon === REWARD_ICONS[1])?.value ?? 0) !== 0 && (
        <div className="relative flex flex-col items-center justify-center gap-2">
          <img
            src={"https://cdn.navalabs.io/double-icon_4iL4Vk.png"}
            alt={"Double Icon"}
            className={"h-4 absolute -top-1 -right-1"}
            style={{ imageRendering: "pixelated" }}
          />
          <img
            src={REWARD_ICONS[1]}
            alt={"Double Trophy"}
            className={iconSize}
            style={{ imageRendering: "pixelated" }}
          />
          <span
            className={cn(
              `${textSize} font-bold -mt-3.5 text-emerald-300 leading-4`,
            )}
            style={{
              textShadow: generateTextShadow(0.75, "var(--color-emerald-700)"),
            }}
          >
            +{prizes.find((p) => p.icon === REWARD_ICONS[1])?.value ?? 0}
          </span>
        </div>
      )}
    </div>
  );
}
